/**
 * Клиентский журнал сроков обучения (`/training`, canon service-training, контур «своя организация»).
 * Светофор-счётчики, колонки по профилю, строки-сотрудники, фильтры и выгрузка — общий компонент
 * TrainingJournal (clientApi: trainingApi.journal, только свои сотрудники). «Напомнить» клиенту не
 * показывается. Кнопка «Заказать обучение» спрашивает, заполнить ли заявку по срокам
 * (training-order-entry-points), создаёт черновик и ведёт в заявку.
 * Над журналом — надпись об абонентском обслуживании по обучению, пока оно активно
 * (training-subscription).
 */
import { useEffect, useState } from 'react'
import {
  trainingApi,
  type JournalResponse,
  type TrainingSubscription,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { TrainingJournal } from '@/training/training-journal'
import { TrainingSubscriptionNotice } from '@/training/training-subscription'
import { useTrainingOrderStart } from '@/training/training-order-start'
import { useOptionalAuth } from '@/app/auth-context'
import { PageHead } from '@/design-system/components/layout/page-head'
import { Button } from '@/design-system/components/primitives/button'
import { Alert } from '@/design-system/components/feedback/alert'

export function TrainingJournalPage() {
  const [data, setData] = useState<JournalResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [subscription, setSubscription] = useState<TrainingSubscription | null>(null)

  // Надпись об абонентском обслуживании — дополнение к журналу: сбой её запроса журнал не
  // закрывает и ошибкой не показывается, надписи просто нет.
  useEffect(() => {
    const controller = new AbortController()
    trainingApi
      .subscription(controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setSubscription(res.trainingSubscription)
      })
      .catch(() => undefined)
    return () => controller.abort()
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    trainingApi
      .journal(controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setData(res)
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(getErrorMessage(err, 'Не удалось загрузить журнал'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [])

  // «Заказать обучение» — с вопросом «Заполнить по сотрудникам с истекающим и просроченным
  // обучением?» (training-order-entry-points; ТЗ п.2.2).
  const individual = useOptionalAuth()?.context?.user.role === 'individual'
  const trainingStart = useTrainingOrderStart(individual)

  return (
    <div>
      <PageHead
        title="Обучение"
        subtitle="Сроки обучения по сотрудникам вашей организации"
        actions={
          <Button onClick={() => trainingStart.start()} disabled={trainingStart.starting}>
            Заказать обучение
          </Button>
        }
      />

      <TrainingSubscriptionNotice subscription={subscription} className="mb-4" />

      {trainingStart.error ? (
        <Alert variant="danger" className="mb-4">
          {trainingStart.error}
        </Alert>
      ) : null}
      {trainingStart.dialog}

      {error ? (
        <Alert variant="danger">{error}</Alert>
      ) : loading || !data ? (
        <p className="py-10 text-center text-txt-secondary">Загрузка…</p>
      ) : (
        <TrainingJournal data={data} employeeHref={(personId) => `/staff/${personId}`} />
      )}
    </div>
  )
}
